import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import {
  GET_MY_COMPANY_HOLIDAYS,
  GET_MY_SCHEDULE,
  GET_MY_SCHEDULE_OVERRIDES,
  GET_MY_VACATIONS,
} from '../../graphql/my-availability-queries';
import MyDayOverrideModal from './MyDayOverrideModal';
import {
  isCalendarDateInRange,
  isApprovedVacation,
  toCalendarDateKey,
} from '../../utils/availabilityDateUtils';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const STATUS_STYLES = {
  WORKING: 'bg-sky-100 border-sky-300 text-sky-900 hover:border-sky-500',
  WORKING_REMOTE: 'bg-purple-100 border-purple-300 text-purple-900 hover:border-purple-500',
  OFF: 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-400',
  HOLIDAY: 'bg-red-100 border-red-300 text-red-900',
  VACATION: 'bg-yellow-100 border-yellow-300 text-yellow-900',
  NOT_SET: 'bg-white border-dashed border-gray-200 text-gray-400 hover:border-sky-400',
};

const STATUS_LABELS = {
  WORKING: 'On-Island',
  WORKING_REMOTE: 'Remote',
  OFF: 'Off',
  HOLIDAY: 'Holiday',
  VACATION: 'Time Off',
  NOT_SET: '—',
};

function MyMonthlyCalendar({ refreshKey }) {
  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()));
  const [activeDate, setActiveDate] = useState(null);

  const monthStart = useMemo(() => startOfMonth(currentMonth), [currentMonth]);
  const monthEnd = useMemo(() => endOfMonth(currentMonth), [currentMonth]);
  const gridStart = useMemo(() => startOfWeek(monthStart, { weekStartsOn: 1 }), [monthStart]);
  const gridEnd = useMemo(() => endOfWeek(monthEnd, { weekStartsOn: 1 }), [monthEnd]);
  const calendarDays = useMemo(
    () => eachDayOfInterval({ start: gridStart, end: gridEnd }),
    [gridStart, gridEnd]
  );

  const { data: scheduleData, refetch: refetchSchedule } = useQuery(GET_MY_SCHEDULE, {
    fetchPolicy: 'cache-and-network',
  });
  const { data: vacationsData, refetch: refetchVacations } = useQuery(GET_MY_VACATIONS, {
    variables: {
      startDate: gridStart.toISOString(),
      endDate: gridEnd.toISOString(),
    },
    fetchPolicy: 'cache-and-network',
  });
  const { data: holidaysData, refetch: refetchHolidays } = useQuery(GET_MY_COMPANY_HOLIDAYS, {
    variables: {
      startDate: gridStart.toISOString(),
      endDate: gridEnd.toISOString(),
    },
    fetchPolicy: 'cache-and-network',
  });
  const { data: overridesData, refetch: refetchOverrides } = useQuery(GET_MY_SCHEDULE_OVERRIDES, {
    variables: {
      startDate: gridStart.toISOString(),
      endDate: gridEnd.toISOString(),
    },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (refreshKey === undefined) return;
    refetchSchedule();
    refetchVacations();
    refetchHolidays();
    refetchOverrides();
  }, [refreshKey, refetchSchedule, refetchVacations, refetchHolidays, refetchOverrides]);

  const schedulePattern = scheduleData?.mySchedule || [];
  const vacations = vacationsData?.myVacations || [];
  const holidays = holidaysData?.myCompanyHolidays || [];
  const overrides = overridesData?.myScheduleOverrides || [];
  const hasSchedulePattern = schedulePattern.length > 0;

  const overrideMap = useMemo(() => {
    const map = new Map();
    overrides.forEach((entry) => {
      const key = toCalendarDateKey(entry.date);
      if (key) map.set(key, entry);
    });
    return map;
  }, [overrides]);

  const today = new Date();

  const handlePreviousMonth = () => setCurrentMonth((prev) => subMonths(prev, 1));
  const handleNextMonth = () => setCurrentMonth((prev) => addMonths(prev, 1));
  const handleThisMonth = () => setCurrentMonth(startOfMonth(new Date()));

  const getDayInfo = useCallback(
    (date) => {
      const dateKey = toCalendarDateKey(date);
      const holiday = holidays.find((entry) => toCalendarDateKey(entry.date) === dateKey);
      if (holiday) {
        return {
          status: 'HOLIDAY',
          label: holiday.name,
          isOverride: false,
          isBlocked: true,
          blockedReason: `${holiday.name} is a company holiday.`,
        };
      }
      const vacation = vacations.find((entry) =>
        isApprovedVacation(entry)
        && isCalendarDateInRange(date, entry.startDate, entry.endDate)
      );
      if (vacation) {
        return {
          status: 'VACATION',
          label: vacation.vacationType || 'Time Off',
          isOverride: false,
          isBlocked: true,
          blockedReason: "You've requested time off for this day.",
        };
      }
      const override = overrideMap.get(toCalendarDateKey(date));
      if (override) {
        if (!override.isWorking) {
          return {
            status: 'OFF',
            label: 'Off',
            isOverride: true,
            isWorking: false,
            isRemote: false,
            notes: override.notes || '',
          };
        }
        return {
          status: override.isRemote ? 'WORKING_REMOTE' : 'WORKING',
          label: override.isRemote ? 'Remote' : 'On-Island',
          isOverride: true,
          isWorking: true,
          isRemote: override.isRemote === true,
          notes: override.notes || '',
        };
      }
      if (!hasSchedulePattern) {
        return { status: 'NOT_SET', label: 'Not set', isOverride: false };
      }
      const dayOfWeek = date.getDay();
      const pattern = schedulePattern.find((entry) => entry.dayOfWeek === dayOfWeek);
      if (!pattern) return { status: 'OFF', label: 'Off', isOverride: false };
      if (!pattern.isWorking) return { status: 'OFF', label: 'Off', isOverride: false };
      return pattern.isRemote === true
        ? { status: 'WORKING_REMOTE', label: 'Remote', isOverride: false }
        : { status: 'WORKING', label: 'On-Island', isOverride: false };
    },
    [holidays, vacations, overrideMap, hasSchedulePattern, schedulePattern]
  );

  const handleOpenDay = (date) => setActiveDate(date);
  const handleCloseDay = () => setActiveDate(null);
  const handleDaySaved = () => {
    refetchOverrides();
    setActiveDate(null);
  };

  const activeDayInfo = activeDate ? getDayInfo(activeDate) : null;
  const activeEffectiveStatus = activeDayInfo
    ? {
        isWorking:
          activeDayInfo.status === 'WORKING' || activeDayInfo.status === 'WORKING_REMOTE',
        isRemote: activeDayInfo.status === 'WORKING_REMOTE',
        notes: activeDayInfo.notes || '',
      }
    : null;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePreviousMonth}
              aria-label="Previous month"
              className="p-2 rounded-md hover:bg-gray-100 text-gray-500"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h2 className="text-lg font-semibold text-gray-900 min-w-[160px] text-center">
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
            <button
              type="button"
              onClick={handleNextMonth}
              aria-label="Next month"
              className="p-2 rounded-md hover:bg-gray-100 text-gray-500"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button
              type="button"
              onClick={handleThisMonth}
              className="ml-2 px-3 py-1.5 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              This Month
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded bg-sky-200 border border-sky-300"></span>
              On-Island
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded bg-purple-200 border border-purple-300"></span>
              Remote
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded bg-gray-100 border border-gray-300"></span>
              Off
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded bg-yellow-200 border border-yellow-300"></span>
              Time Off
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded bg-red-200 border border-red-300"></span>
              Holiday
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded bg-white border border-dashed border-gray-300"></span>
              Not Set
            </span>
          </div>
        </div>

        <p className="text-xs text-gray-500 mb-3">
          Click any day to override your schedule for that specific date.
        </p>

        <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-gray-500 mb-1">
          {DAY_LABELS.map((label) => (
            <div key={label} className="py-1">{label}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((day) => {
            const inMonth = isSameMonth(day, currentMonth);
            const isToday = isSameDay(day, today);
            const info = getDayInfo(day);
            const baseStyle = STATUS_STYLES[info.status] || STATUS_STYLES.OFF;
            const todayRing = isToday ? 'ring-2 ring-sky-500' : '';
            const mutedStyle = inMonth ? '' : 'opacity-40';
            const hasNote = Boolean(info.notes && info.notes.trim());
            const cellLabel = `${format(day, 'EEE, MMM d, yyyy')} — ${info.label}${
              info.isOverride ? ' (override)' : ''
            }${hasNote ? `\nNote: ${info.notes}` : ''}`;
            return (
              <button
                type="button"
                key={day.toISOString()}
                onClick={() => handleOpenDay(day)}
                aria-label={cellLabel}
                title={cellLabel}
                className={`relative border rounded-md p-1.5 min-h-[60px] sm:min-h-[72px] flex flex-col justify-between text-left transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500 ${baseStyle} ${todayRing} ${mutedStyle}`}
              >
                <div className="flex items-start justify-between">
                  <span className="text-xs font-semibold">{format(day, 'd')}</span>
                  <div className="flex items-center gap-1">
                    {hasNote && (
                      <svg
                        className="w-3 h-3 text-purple-600"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h6v2H9V8zm-4 4h2v2H5v-2zm4 0h4v2H9v-2z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    {info.isOverride && (
                      <span
                        className="w-1.5 h-1.5 rounded-full bg-purple-600"
                        title="Custom override"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                </div>
                <div className="text-[10px] sm:text-xs uppercase tracking-wide truncate">
                  {STATUS_LABELS[info.status]}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <MyDayOverrideModal
        isOpen={Boolean(activeDate)}
        date={activeDate}
        effectiveStatus={activeEffectiveStatus}
        hasOverride={activeDayInfo?.isOverride === true}
        isBlocked={activeDayInfo?.isBlocked === true}
        blockedReason={activeDayInfo?.blockedReason}
        onClose={handleCloseDay}
        onSaved={handleDaySaved}
      />
    </div>
  );
}

export default MyMonthlyCalendar;
